import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import { apiService } from "../services/api";

const statusLabels = {
  awaiting_billability: "Validation en attente",
  billability_no_recipients: "Aucun destinataire de validation",
  billable: "Facturable",
  not_billable: "Non facturable",
  invoice_notified: "Notification facture envoyee",
  invoice_no_recipients: "Aucun destinataire facture",
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(date);
};

const IterationEndNotificationPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const loadEvent = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.get(`/iteration-end-notifications/${eventId}`);
      setEvent(data);
      setError(null);
    } catch (err) {
      setError(err.message || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const decisionLabel = useMemo(() => {
    if (!event) return "";
    if (event.is_billable === true) return "Facturable";
    if (event.is_billable === false) return "Non facturable";
    return "Non decide";
  }, [event]);

  const submitDecision = async (isBillable) => {
    try {
      setSaving(isBillable ? "yes" : "no");
      const data = await apiService.post(`/iteration-end-notifications/${eventId}/decision`, {
        is_billable: isBillable,
      });
      setEvent(data);
      setError(null);
    } catch (err) {
      setError(err.message || "Validation impossible");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-3xl items-center justify-center">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-slate-500" />
        <span className="text-sm text-slate-600">Chargement de la validation...</span>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="mx-auto max-w-3xl py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mb-2 h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl py-8">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Fin d'iteration
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              {event.project_name}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {event.iteration_name} - fin {formatDate(event.iteration_end_date)}
            </p>
          </div>
          <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {statusLabels[event.status] || event.status}
          </span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-400">Decision</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">{decisionLabel}</p>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-400">Date notification</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">
              {formatDate(event.notification_date)}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {event.can_decide ? (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => submitDecision(true)}
              disabled={Boolean(saving)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving === "yes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Facturable
            </button>
            <button
              type="button"
              onClick={() => submitDecision(false)}
              disabled={Boolean(saving)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            >
              {saving === "no" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              Non facturable
            </button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-slate-500">
            Cette validation n'est plus en attente ou vous n'etes pas destinataire de la demande.
          </p>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => navigate(`/project/${event.project_id}`)}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Ouvrir le projet
          </button>
        </div>
      </div>
    </div>
  );
};

export default IterationEndNotificationPage;
